// Beside the greeting on Home: the time, the weather now and a compass. The compass shows where the wind comes from
// (what matters on the water); on a phone it can also turn with the way the person faces. The place is the company's
// home port (Settings → Home) or this device when Location is on. Weather can be hidden in Settings; the time stays.

import { useEffect, useState } from "react";
import { T, dateLocale, currentLang } from "../lib/i18n";
import { useHomePrefs } from "../lib/homePrefs";
import { useWeather, type WeatherNow } from "../lib/weather";
import { useStudio } from "./ui";
import { vera } from "../api/vera";

function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = window.setInterval(() => setNow(new Date()), 20_000); return () => window.clearInterval(t); }, []);
  return now;
}

type OrientationWithCompass = DeviceOrientationEvent & { webkitCompassHeading?: number };
type OrientationCtor = typeof DeviceOrientationEvent & { requestPermission?: () => Promise<"granted" | "denied"> };

/** The way the phone faces (0 = north), once the person turns it on. Null on computers. */
function useHeading(on: boolean) {
  const [heading, setHeading] = useState<number | null>(null);
  useEffect(() => {
    if (!on) { setHeading(null); return; }
    const read = (e: Event) => {
      const o = e as OrientationWithCompass;
      const h = typeof o.webkitCompassHeading === "number" ? o.webkitCompassHeading : o.absolute && typeof o.alpha === "number" ? 360 - o.alpha : null;
      if (h !== null) setHeading(Math.round(h) % 360);
    };
    window.addEventListener("deviceorientationabsolute", read);
    window.addEventListener("deviceorientation", read);
    return () => { window.removeEventListener("deviceorientationabsolute", read); window.removeEventListener("deviceorientation", read); };
  }, [on]);
  return heading;
}

const phone = () => typeof window !== "undefined" && "DeviceOrientationEvent" in window && /Android|iPhone|iPad/i.test(navigator.userAgent);

function Compass({ windFrom, heading, size = 54 }: { windFrom?: number | null; heading: number | null; size?: number }) {
  const turn = heading === null ? 0 : -heading;           // the rose turns so N points north
  const toward = windFrom == null ? null : (windFrom + 180) % 360; // the wind blows toward here
  return (
    <svg width={size} height={size} viewBox="0 0 60 60" role="img" aria-label={windFrom == null ? T("Compass") : `${T("Wind from")} ${windFrom}°`}>
      <g transform={`rotate(${turn} 30 30)`}>
        <circle cx="30" cy="30" r="27" fill="rgba(88, 200, 220, 0.10)" stroke="rgba(120, 214, 230, 0.55)" strokeWidth="1.5" />
        {["N", "E", "S", "W"].map((d, i) => (
          <text key={d} x={30 + 20 * Math.sin((i * Math.PI) / 2)} y={30 - 20 * Math.cos((i * Math.PI) / 2) + 3.5} textAnchor="middle" fontSize="9" fontWeight={d === "N" ? 700 : 500} fill={d === "N" ? "#ffc861" : "currentColor"} opacity={d === "N" ? 1 : 0.75}>{d}</text>))}
        {toward !== null && (
          <g transform={`rotate(${toward} 30 30)`}>
            <line x1="30" y1="42" x2="30" y2="17" stroke="#7fe3ee" strokeWidth="2.4" strokeLinecap="round" />
            <path d="M30 12 L25.5 20 L34.5 20 Z" fill="#7fe3ee" />
          </g>)}
      </g>
    </svg>
  );
}

function weatherLine(w: WeatherNow) {
  const wind = `${T("wind")} ${Math.round(w.windSpeed)}${w.windGust ? `–${Math.round(w.windGust)}` : ""} ${w.speedUnit}${w.windFromText ? ` ${T("from")} ${w.windFromText}` : ""}`;
  return { top: `${Math.round(w.temperature)}°${w.unit} · ${w.phrase}`, sub: [wind, w.rainChance ? `${T("rain")} ${w.rainChance}%` : ""].filter(Boolean).join(" · ") };
}

export function GreetingBar({ compact = false }: { compact?: boolean } = {}) {
  const { go } = useStudio();
  const { prefs, shows } = useHomePrefs();
  const now = useClock();
  const showWeather = shows("weather") && vera.mode === "live";
  const state = useWeather(prefs.place, showWeather);
  const [turning, setTurning] = useState(false);
  const heading = useHeading(turning);

  async function turnWithPhone() {
    const ctor = (window as unknown as { DeviceOrientationEvent?: OrientationCtor }).DeviceOrientationEvent;
    try { if (ctor?.requestPermission && (await ctor.requestPermission()) !== "granted") return; } catch { return; }
    setTurning((v) => !v);
  }

  const w = state?.weather ?? null;
  const line = w ? weatherLine(w) : null;
  const time = now.toLocaleTimeString(dateLocale(currentLang()), { hour: "2-digit", minute: "2-digit" });
  // The top bar's chip (top right, every page): time, temperature and sky, the wind on a small compass. One line; on a
  // phone only the time and the temperature. The full words are in its tooltip.
  if (compact) return (
    <div className="topclock" title={line ? `${line.top} · ${line.sub}${w?.next ? ` · ${T("Later")} ${w.next}` : ""}` : time} aria-label={T("Time and weather")}>
      <b className="num">{time}</b>
      {showWeather && (w ? (
        <>
          <span className="topclock-w">{Math.round(w.temperature)}°{w.unit}<span className="topclock-sky"> · {w.phrase}</span></span>
          <button className="greet-compass" onClick={phone() ? () => void turnWithPhone() : undefined} aria-pressed={phone() ? turning : undefined} aria-label={w.windFromText ? `${T("Wind from")} ${w.windFromText}` : T("Compass")}>
            <Compass windFrom={w.windFrom} heading={heading} size={24} />
          </button>
        </>
      ) : state?.reason === "noplace" ? <button className="linkbtn small topclock-set" onClick={() => go("settings")}>{T("Set weather")}</button> : null)}
    </div>
  );
  return (
    <div className="greetbar" aria-label={T("Time and weather")}>
      <div className="greet-time">
        <b>{now.toLocaleTimeString(dateLocale(currentLang()), { hour: "2-digit", minute: "2-digit" })}</b>
        <span className="faint small">{state?.where ?? ""}</span>
      </div>
      {showWeather && (line ? (
        <>
          <div className="greet-weather"><b>{line.top}</b><span className="faint small">{line.sub}</span>{w?.next && <span className="faint small">{T("Later")} {w.next}</span>}</div>
          <button className="greet-compass" onClick={phone() ? () => void turnWithPhone() : undefined} title={phone() ? T("Turn the compass with your phone") : w?.windFromText ? `${T("Wind from")} ${w.windFromText}` : T("Compass")} aria-pressed={phone() ? turning : undefined}>
            <Compass windFrom={w?.windFrom} heading={heading} />
          </button>
        </>
      ) : state?.reason === "noplace" ? (
        <button className="btn sm" onClick={() => go("settings")}>{T("Set your home port for the weather")}</button>
      ) : state?.reason === "error" ? <span className="faint small">{T("The weather isn't available right now.")}</span> : <span className="faint small">{T("Loading…")}</span>)}
    </div>
  );
}
